/**
 * orgChartLayout — pure tree-layout maths for the Org Chart feature.
 *
 * Ported from the Deckorate prototype's `orgChartBuilder.js` (`_computeLayout` /
 * `_computeConnectors`), with no Office.js dependency so it can be unit-tested
 * directly. `buildLayout` turns a flat `OrgPerson[]` into positioned cards
 * (POINTS) and the L-shaped connector segments joining each parent to its
 * children.
 */

import type { Connector, OrgLayout, OrgPerson, PositionedNode } from "../../../types";

/* ── Slide geometry (widescreen 13.33" × 7.5" = 960 × 540 pt) ── */
export const SLIDE_WIDTH = 960;
export const SLIDE_HEIGHT = 540;
/** Slide-edge margin, points. */
export const MARGIN = 36;

/** Floors applied when a chart is scaled down to fit the slide (points). */
const MIN_CARD_W = 50;
const MIN_CARD_H = 25;
const MIN_H_GAP = 6;
const MIN_V_GAP = 12;

/** Card sizing tiers by head-count — bigger cards for smaller orgs. */
interface CardSize {
  cardW: number;
  cardH: number;
  hGap: number;
  vGap: number;
}

function cardSizeFor(totalPeople: number): CardSize {
  if (totalPeople <= 5) return { cardW: 180, cardH: 70, hGap: 30, vGap: 50 };
  if (totalPeople <= 10) return { cardW: 155, cardH: 60, hGap: 25, vGap: 45 };
  if (totalPeople <= 20) return { cardW: 130, cardH: 50, hGap: 22, vGap: 43 };
  return { cardW: 110, cardH: 42, hGap: 16, vGap: 35 };
}

interface LayoutNode extends OrgPerson {
  children: LayoutNode[];
  level: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Assign a `level` to every node via breadth-first traversal from the roots.
 * A `visited` set guards against cycles in `parentId` (which would otherwise
 * loop forever) and guarantees every node is reached exactly once.
 */
function assignLevels(roots: LayoutNode[]): void {
  const queue = [...roots];
  const visited = new Set<number>();
  for (const r of roots) {
    r.level = 0;
    visited.add(r.id);
  }
  while (queue.length > 0) {
    const node = queue.shift()!;
    for (const child of node.children) {
      if (visited.has(child.id)) continue; // cycle guard
      visited.add(child.id);
      child.level = node.level + 1;
      queue.push(child);
    }
  }
}

/**
 * Build the node graph: a lookup map plus the list of roots. Anyone without a
 * resolvable parent is a root; a node that lists itself as its own parent is
 * also treated as a root (never its own child).
 */
function buildForest(people: readonly OrgPerson[]): {
  nodeMap: Map<number, LayoutNode>;
  roots: LayoutNode[];
} {
  const nodeMap = new Map<number, LayoutNode>();
  for (const p of people) {
    nodeMap.set(p.id, { ...p, children: [], level: 0, x: 0, y: 0, width: 0, height: 0 });
  }

  const roots: LayoutNode[] = [];
  for (const p of people) {
    const node = nodeMap.get(p.id)!;
    const parent =
      p.parentId != null && p.parentId !== p.id ? nodeMap.get(p.parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return { nodeMap, roots };
}

/** Bucket every node into an array indexed by its level. */
function groupByLevel(allNodes: readonly LayoutNode[], maxLevel: number): LayoutNode[][] {
  const levels: LayoutNode[][] = Array.from({ length: maxLevel + 1 }, () => []);
  for (const n of allNodes) levels[n.level]!.push(n);
  return levels;
}

/** Order one level so children follow their parent's left-to-right order,
 * with any orphan appended (safety net). */
function orderLevel(current: LayoutNode[], parentLevel: LayoutNode[]): LayoutNode[] {
  const ordered: LayoutNode[] = [];
  for (const parent of parentLevel) {
    for (const child of parent.children) {
      if (current.includes(child)) ordered.push(child);
    }
  }
  for (const n of current) {
    if (!ordered.includes(n)) ordered.push(n);
  }
  return ordered;
}

/** Re-order every level (top-down) so children sit under their parents. */
function orderChildrenUnderParents(levels: LayoutNode[][], maxLevel: number): LayoutNode[][] {
  for (let l = 1; l <= maxLevel; l++) {
    levels[l] = orderLevel(levels[l]!, levels[l - 1]!);
  }
  return levels;
}

/**
 * Shrink the card size uniformly if the widest level or the depth overflows the
 * usable slide area. Scales from the head-count tier's own baseline; floors keep
 * cards legible. Returns the original size when everything already fits.
 */
function fitCardSize(size: CardSize, levels: LayoutNode[][], maxLevel: number): CardSize {
  const availW = SLIDE_WIDTH - 2 * MARGIN;
  const availH = SLIDE_HEIGHT - 2 * MARGIN;
  const maxNodesInLevel = levels.reduce((m, lvl) => Math.max(m, lvl.length), 0);

  const neededW = maxNodesInLevel * size.cardW + (maxNodesInLevel - 1) * size.hGap;
  const neededH = (maxLevel + 1) * size.cardH + maxLevel * size.vGap;
  const scaleW = neededW > availW ? availW / neededW : 1;
  const scaleH = neededH > availH ? availH / neededH : 1;
  const scale = Math.min(scaleW, scaleH, 1);
  if (scale >= 1) return size;

  return {
    cardW: Math.max(MIN_CARD_W, Math.round(size.cardW * scale)),
    cardH: Math.max(MIN_CARD_H, Math.round(size.cardH * scale)),
    hGap: Math.max(MIN_H_GAP, Math.round(size.hGap * scale)),
    vGap: Math.max(MIN_V_GAP, Math.round(size.vGap * scale)),
  };
}

/** Place one level's cards along the X axis (top-down layout). */
function placeLevelTopDown(nodes: LayoutNode[], level: number, card: CardSize): void {
  const availW = SLIDE_WIDTH - 2 * MARGIN;
  const totalW = nodes.length * card.cardW + (nodes.length - 1) * card.hGap;
  const startX = MARGIN + (availW - totalW) / 2;
  const y = MARGIN + level * (card.cardH + card.vGap);
  nodes.forEach((node, i) => {
    node.x = startX + i * (card.cardW + card.hGap);
    node.y = y;
    node.width = card.cardW;
    node.height = card.cardH;
  });
}

/** Place one level's cards along the Y axis (left-right layout). */
function placeLevelLeftRight(nodes: LayoutNode[], level: number, card: CardSize): void {
  const availH = SLIDE_HEIGHT - 2 * MARGIN;
  const totalH = nodes.length * card.cardH + (nodes.length - 1) * card.vGap;
  const startY = MARGIN + (availH - totalH) / 2;
  const x = MARGIN + level * (card.cardW + card.hGap);
  nodes.forEach((node, i) => {
    node.x = x;
    node.y = startY + i * (card.cardH + card.vGap);
    node.width = card.cardW;
    node.height = card.cardH;
  });
}

/** Position every level for the chosen direction. */
function placeLevels(
  levels: LayoutNode[][],
  direction: OrgLayout,
  maxLevel: number,
  card: CardSize,
): void {
  for (let l = 0; l <= maxLevel; l++) {
    if (direction === "top-down") placeLevelTopDown(levels[l]!, l, card);
    else placeLevelLeftRight(levels[l]!, l, card);
  }
}

/** Strip a LayoutNode down to the public PositionedNode shape. */
function toPositioned(node: LayoutNode): PositionedNode {
  return {
    id: node.id,
    name: node.name,
    title: node.title,
    x: node.x,
    y: node.y,
    width: node.width,
    height: node.height,
    parentId: node.parentId,
    photoDataUrl: node.photoDataUrl ?? null,
  };
}

/**
 * Emit nodes parents-before-children (depth-first from each root), the order the
 * builder relies on for connectors. A `seen` set guards cycles, and a final
 * sweep over `allNodes` ensures a cycle-stranded node is still emitted.
 */
function flattenParentsFirst(
  roots: readonly LayoutNode[],
  allNodes: readonly LayoutNode[],
): PositionedNode[] {
  const result: PositionedNode[] = [];
  const seen = new Set<number>();
  const collect = (node: LayoutNode): void => {
    if (seen.has(node.id)) return; // cycle guard
    seen.add(node.id);
    result.push(toPositioned(node));
    for (const child of node.children) collect(child);
  };
  for (const root of roots) collect(root);
  for (const node of allNodes) collect(node);
  return result;
}

/**
 * Compute card positions and sizes for every person.
 *
 * @returns positioned nodes (parents before their children).
 */
export function computeLayout(
  people: readonly OrgPerson[],
  direction: OrgLayout,
): PositionedNode[] {
  const { nodeMap, roots } = buildForest(people);
  assignLevels(roots);

  const allNodes = [...nodeMap.values()];
  const maxLevel = allNodes.reduce((m, n) => Math.max(m, n.level), 0);

  const levels = orderChildrenUnderParents(groupByLevel(allNodes, maxLevel), maxLevel);
  const card = fitCardSize(cardSizeFor(allNodes.length), levels, maxLevel);
  placeLevels(levels, direction, maxLevel, card);

  return flattenParentsFirst(roots, allNodes);
}

/**
 * Build the 3-segment L-connectors joining every child to its parent.
 * `top-down` connects parent-bottom → child-top; `left-right` connects
 * parent-right → child-left.
 */
export function computeConnectors(
  positioned: readonly PositionedNode[],
  direction: OrgLayout,
): Connector[] {
  const posMap = new Map<number, PositionedNode>();
  for (const node of positioned) posMap.set(node.id, node);

  const segments: Connector[] = [];
  for (const node of positioned) {
    if (node.parentId == null) continue;
    const parent = posMap.get(node.parentId);
    if (!parent) continue;

    if (direction === "top-down") {
      const px = parent.x + parent.width / 2;
      const py = parent.y + parent.height;
      const cx = node.x + node.width / 2;
      const cy = node.y;
      const midY = (py + cy) / 2;
      segments.push({ x1: px, y1: py, x2: px, y2: midY });
      if (Math.abs(px - cx) > 1) segments.push({ x1: px, y1: midY, x2: cx, y2: midY });
      segments.push({ x1: cx, y1: midY, x2: cx, y2: cy });
    } else {
      const px = parent.x + parent.width;
      const py = parent.y + parent.height / 2;
      const cx = node.x;
      const cy = node.y + node.height / 2;
      const midX = (px + cx) / 2;
      segments.push({ x1: px, y1: py, x2: midX, y2: py });
      if (Math.abs(py - cy) > 1) segments.push({ x1: midX, y1: py, x2: midX, y2: cy });
      segments.push({ x1: midX, y1: cy, x2: cx, y2: cy });
    }
  }
  return segments;
}

/**
 * Initials from a name (up to 2 chars): "Jane Smith" → "JS", "John" → "J",
 * "Mary Jane Watson" → "MW". Empty/blank → "?".
 */
export function getInitials(name: string): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]![0]!.toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * Relative luminance of a 6-digit hex colour (no "#"), range 0–1. Used to pick
 * legible initials text against the avatar fill.
 */
export function luminance(hex: string): number {
  const clean = hex.replace(/^#/, "");
  const r = Number.parseInt(clean.slice(0, 2), 16) / 255;
  const g = Number.parseInt(clean.slice(2, 4), 16) / 255;
  const b = Number.parseInt(clean.slice(4, 6), 16) / 255;
  return 0.299 * r + 0.587 * g + 0.114 * b;
}
